import {
  BoldOutlined,
  CaretDownFilled,
  CloseOutlined,
  DeleteOutlined,
  FileImageOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  PaperClipOutlined,
  QuestionCircleOutlined,
  SmileOutlined,
  StrikethroughOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';

import { ReactComponent as MagicPencilIcon } from 'assets/icons/magic-pen.svg';

import {
  Alert,
  Button,
  Chip,
  Heading,
  Input,
  Spinner,
  Tooltip,
} from '@mm/components';
import { useAutomationsCRUDActions } from '@mm/state/actions/automationActions';
import { useUploadActions } from '@mm/state/actions/uploadActions';

import {
  hideSlideoutMenu,
  useViewActions,
} from '@mm/state/actions/viewActions';
import WidgetModel, {
  EmailConfig,
  WidgetBase,
  WidgetTypes,
} from '@mm/state/model/WidgetModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { colors, getUniqId } from '@mm/utils';
import classNames from 'classnames';
import { debounce, isEqual } from 'lodash';
import { Node } from 'prosemirror-model';
import { EditorView } from 'prosemirror-view';
import 'prosemirror-view/style/prosemirror.css';
import React, {
  ChangeEvent,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { ProseMirror } from 'use-prosemirror';
import { ErrorIcon } from '../../../../../common/component-icons/ErrorIcon/ErrorIcon';
import AttributePicker from '../../../../../common/components/AttributePicker/AttributePicker';
import { EmojiPicker } from '../../../../../common/components/EmojiPicker/EmojiPicker';
import { nameById } from '../../../../../common/components/FilterableSelect/filterable-select-helpers';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import {
  getFormatFromType,
  useCommonWidgetHandlers,
} from '../../../../../common/helpers/widget-helpers/use-common-widget-handlers';
import { useEmailWidgetHandlers } from '../../../../../common/helpers/widget-helpers/use-email-widget-handlers';
import { useDetectOutsideClicks } from '../../../../../common/hooks/use-detect-outside-clicks';
import { useFreeTrialUpgradeModal } from '../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import AttributeContainingTextInput from '../../../AttributeContainingTextInput/AttributeContainingTextInput';
import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';
import { useEditor } from './use-editor-old';
import { EMAIL_TEMPLATES } from './EmailTemplates';
import { LinkPrompt } from './editor/components/Link/LinkPrompt';
import css from './SlideoutEmail.module.css';
import { ReactComponent as BracketsOutlined } from './svg/brackets.svg';
import { useEditorImageHandlers } from './editor/hooks/use-editor-image-handlers';
import { SlideOutMenuType } from '@mm/state/interfaces/view';
import isEmail from 'validator/lib/isEmail';
import { type } from 'os';
import { SmartSendingInfo } from './SmartSendingInfo/SmartSendingInfo';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';

const MAX_FILE_SIZE_IN_MB = 8;
const MAX_FILE_SIZE = MAX_FILE_SIZE_IN_MB * 1024 * 1024;
const MAX_ATTACHMENTS = 8;
const ACCEPTED_FILE_TYPES = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.jpg',
  '.jpeg',
  '.png',
  '.bmp',
  '.gif',
  '.tif',
  '.tiff',
  '.psd',
  '.al',
  '.txt',
  '.rtf',
  '.mobi',
  '.epub',
];
// For a newer version of react-dropzone, use:
// const ACCEPTED_FILE_TYPES = {
//   'application/pdf': ['.pdf'],
//   'application/msword': ['.doc'],
//   'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
//     '.docx',
//   ],
//   'application/vnd.ms-excel': ['.xls'],
//   'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
//     '.xlsx',
//   ],
//   'application/vnd.ms-powerpoint': ['.ppt'],
//   'application/vnd.openxmlformats-officedocument.presentationml.presentation': [
//     '.pptx',
//   ],
//   'image/jpeg': ['.jpg', '.jpeg'],
//   'image/png': ['.png'],
//   'image/bmp': ['.bmp'],
//   'image/gif': ['.gif'],
//   'image/tiff': ['.tif', '.tiff'],
//   'image/vnd.adobe.photoshop': ['.psd'],
//   'application/vnd.adobe.illustrator': ['.ai'],
//   'text/plain': ['.txt'],
//   'text/rtf': ['.rtf'],
//   'application/x-mobipocket-ebook': ['.mobi'],
//   'application/epub+zip': ['.epub'],
// };

export type ISlideoutEmail = {
  className?: string;
  chatbotId?: number;
  dialogueId?: number;
  title?: ReactNode;
  showSmartSendingInfo?: boolean;
  navigateWidget?: WidgetModel;
};

interface Attachment {
  uniqId: string;

  name: string;

  added?: boolean;
  deleted?: boolean;
  uploadPromise?: Promise<void>;
  uploadError?: string;

  id?: number;
}

export type ISlideoutEmailWithWidget = ISlideoutEmail & {
  widget: WidgetModel;
};

export const useSlideoutOnClose = (
  onClose: () => Promise<unknown> = async () => {},
  menuType?: SlideOutMenuType,
) => {
  const [wrapperRef, setWrapperRef] = useState<HTMLElement | null>(null);

  const reduxDispatch: Dispatch<StoreState> = useDispatch();
  const { openNewConfirmModal } = useModalActions();

  const menu = useSelector((state: StoreState) => state.view.slideoutMenu);
  const lastSlideout = menu[menu.length - 1];

  const close = () => reduxDispatch(hideSlideoutMenu());

  useDetectOutsideClicks(async e => {
    if (menuType && lastSlideout.menuType !== menuType) {
      return;
    }

    if (Array.from((e.target as Element)?.classList)?.includes('js-overlay')) {
      try {
        await onClose();
        close();
      } catch (err) {}
    }
  }, wrapperRef);

  const setRef = (ref: HTMLElement) => {
    setWrapperRef(ref);
  };

  const onBeforeClose = (initialValue, updatedValue) => {
    const hasNoChanges = isEqual(initialValue, updatedValue);
    if (hasNoChanges) {
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      openNewConfirmModal({
        title: 'You have unsaved changes',
        subtitle: 'Are you sure you want to leave without saving?',
        onReject: async () => reject(),
        onResolve: async close => {
          close();
          resolve(true);
        },
        resolveButton: {
          children: "Yes, I'm sure",
        },
      });
    });
  };

  return {
    setRef,
    wrapperRef,
    close,
    onBeforeClose,
  };
};

const SlideoutEmailTemplateList: React.FC<ISlideoutEmailWithWidget> = props => {
  const { className, widget } = props;
  const { setRef, close } = useSlideoutOnClose();

  const [name, setName] = useState('');

  const commonWidgetHandlers = useCommonWidgetHandlers();

  const updateWidget = async (emailTemplateId: string) => {
    const template = EMAIL_TEMPLATES[emailTemplateId];
    if (!template) {
      return;
    }

    await commonWidgetHandlers.update({
      ...widget,
      config: {
        ...widget.config,
        subject: template.subject,
        body: template.body,
        previewText: template.previewText,
      },
    });
  };

  return (
    <Menu
      className={classNames(className, css.container, css.listContainer)}
      setRef={setRef}
    >
      <div className={css.headerContainer}>
        <span className={css.close} onClick={close}>
          <CloseOutlined />
        </span>

        <h1 className={css.listHeading}>Searching for Inspiration?</h1>
      </div>

      {/* EXPECTED TO BE USED  */}
      {/*<section>*/}
      {/*  <CollapsibleContentContainer*/}
      {/*    orderNumber="1"*/}
      {/*    containerName="Give your email a name so you can refer to it later"*/}
      {/*    stepperStylingPlatform="facebook-inverted"*/}
      {/*    useCircleNumberStyle*/}
      {/*    expandedOnMount*/}
      {/*    hideExpandIcon*/}
      {/*    hideTitleBorder*/}
      {/*  >*/}
      {/*    <Input*/}
      {/*      css={{ container: css.input }}*/}
      {/*      value={name}*/}
      {/*      onChange={e => setName(e.target.value)}*/}
      {/*    />*/}
      {/*  </CollapsibleContentContainer>*/}
      {/*</section>*/}

      <section>
        {/*<CollapsibleContentContainer*/}
        {/*  orderNumber="1"*/}
        {/*  containerName="Pick one of our pre-written email templates to increase email*/}
        {/*  engagement"*/}
        {/*  stepperStylingPlatform="facebook-inverted"*/}
        {/*  useCircleNumberStyle*/}
        {/*  expandedOnMount*/}
        {/*  hideExpandIcon*/}
        {/*  hideTitleBorder*/}
        {/*>*/}
        <div className={css.list}>
          {Object.entries(EMAIL_TEMPLATES).map(([id, template]) => (
            <div className={css.listItem} key={id}>
              <div className={css.listIcon}>
                <template.listIcon />
              </div>
              <div className={css.listText}>
                <span className={css.listTitle}>{template.title}</span>
                <span className={css.listDescription}>
                  {template.listDescription}
                </span>
              </div>
              <Button
                size="sm"
                onClick={() => updateWidget(id)}
                css={{ container: css.button }}
              >
                Choose
              </Button>
            </div>
          ))}
        </div>
        {/*</CollapsibleContentContainer>*/}
      </section>
    </Menu>
  );
};

const compareNodes = debounce(
  (l: Node, r: Node) => {
    console.count('compare');
    return l.toString() !== r.toString();
  },
  300,
  { leading: true, trailing: false },
);

const CC_CAP = 5;
const BCC_CAP = 5;

const extractEmailsFromString = (s: string): string[] => {
  return (
    s
      ?.trim()
      .split(',')
      .map(v => v.trim())
      .filter(v => isEmail(v)) || []
  );
};

const filterDuplicates = (toFilter: string[], refArr: string[]) => {
  return toFilter.filter(v => !refArr.includes(v));
};

const processEmails = (inputString: string, state: string[], cap: number) => {
  const extracted = extractEmailsFromString(inputString);
  const uniq = filterDuplicates(extracted, state);

  return [...state, ...uniq].slice(0, cap);
};

const emailInputPlaceholder = 'Enter email here and press enter to save';

const maxReachText = `You have reached the limit of {count} emails`;

const ccMaxReachText = maxReachText.replace('{count}', String(CC_CAP));
const bccMaxReachText = maxReachText.replace('{count}', String(BCC_CAP));

const SlideoutEmailEditor: React.FC<ISlideoutEmailWithWidget> = props => {
  const {
    title,
    className,
    widget,
    navigateWidget,
    chatbotId,
    showSmartSendingInfo,
  } = props;

  const editorRef = useRef<{ view: EditorView }>(null);

  const automationsCRUDActions = useAutomationsCRUDActions();
  const commonWidgetHandlers = useCommonWidgetHandlers();
  const uploadActions = useUploadActions();

  const attributes = useSelector(
    (state: StoreState) => state.attribute.entities,
  );

  const {
    previewTextValue,
    subjectValue,
    bodyValue,
    onChangeSubject,
    onChangePreviewText,
    onChangeBody,
    setEmailPreviewTextRef,
    setEmailSubjectRef,
    updateInsertAttributeSubject,
    updateInsertAttributePreviewText,
    updateInsertEmojiSubject,
    updateInsertEmojiPreviewText,
  } = useEmailWidgetHandlers({ widget }, false);

  const {
    config: { subject, body, previewText, cc, bcc },
  } = widget as unknown as WidgetBase<EmailConfig>;

  const [ccInputValue, setCcInputValue] = useState('');
  const [bccInputValue, setBccInputValue] = useState('');

  const [ccEmails, setCCEmails] = useState<string[]>(
    extractEmailsFromString(cc),
  );
  const [bccEmails, setBCCEmails] = useState<string[]>(
    extractEmailsFromString(bcc),
  );

  const ccDisabled = ccEmails.length >= CC_CAP;
  const bccDisabled = bccEmails.length >= BCC_CAP;

  const handleCCSubmit = () => {
    const emails = processEmails(ccInputValue, ccEmails, CC_CAP);

    setCCEmails(emails);
    setCcInputValue('');
  };

  const handleCCDelete = (emailToDelete: string) => {
    setCCEmails(ccEmails.filter(v => v !== emailToDelete));
  };

  const handleBCCSubmit = () => {
    const emails = processEmails(bccInputValue, bccEmails, BCC_CAP);
    setBCCEmails(emails);
    setBccInputValue('');
  };

  const handleBCCDelete = (emailToDelete: string) => {
    setBCCEmails(bccEmails.filter(v => v !== emailToDelete));
  };

  const cache = useRef({
    subject,
    body,
    previewText,
  });

  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const showSentFromMMWarning =
    !!activeFacebookPage?.redemption?.freeTrial &&
    !(activeFacebookPage?.subscription?.state === 'future');

  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const hasAccessToCompletions = true; // featureFlags['completions'];

  const modalActions = useModalActions();
  const { openFreeTrialUpgradeModal } = useFreeTrialUpgradeModal();
  const onUpgradeClick = async () => {
    try {
      await onBeforeClose();
      closeSlideout();
      openFreeTrialUpgradeModal();
    } catch {}
  };

  const openSendTestEmailModal = () => {
    if (chatbotId) {
      modalActions.openSendTestEmailModal({
        chatbotId,
        onSubmit: async emails => {
          if (facebookPageId) {
            const emailAttachmentIds = attachments
              .map(a => a.id)
              .filter(a => !!a) as number[];
            await automationsCRUDActions.sendTestEmail({
              emails,
              facebookPageId,
              widgetId: widget.id,
              body: bodyValue,
              subject: subjectValue,
              preview: previewTextValue,
              emailAttachments: emailAttachmentIds.map(id => ({ id })),
            });
          }
        },
      });
    }
  };

  const onSendTestEmailClick = () => {
    if (hasNoChanges) {
      openSendTestEmailModal();
      return;
    }

    modalActions.openNewConfirmModal({
      title: 'You have unsaved changes',
      subtitle: 'Do you want to save your changes before sending test email?',
      onResolve: async () => {
        await onSave();
        openSendTestEmailModal();
      },
      rejectButton: {
        children: 'Continue without saving',
        onClick: async () => openSendTestEmailModal(),
      },
      resolveButton: {
        children: 'Save and go to test',
      },
    });
  };

  const onDeleteClick = () => {
    const deleteCallback = async ok => {
      if (ok && navigateWidget) {
        await commonWidgetHandlers.delete(navigateWidget);
        closeSlideout();
      }
    };

    const headerText = 'Are you sure you want to delete this action?';

    modalActions.openGeneralConfirmModal({
      onCloseHandler: deleteCallback,
      headerText,
      messageText: '',
      okButtonText: 'Delete',
      okButtonStyling: { red: true },
      cancelButtonFirst: true,
    });
  };

  const editor = useEditor(bodyValue);

  const insertBodyAttribute = (value: string) => {
    editor.insertText(`{{${value}}}`, undefined);
    editorRef.current?.view?.focus?.();
  };

  const [busy, setBusy] = useState(false);
  const [waitingForAttachmentUpload, setWaitingForAttachmentUpload] =
    useState(false);
  const onSave = async () => {
    setBusy(true);
    const html = editor.getHTMLStringFromState(editor.state);
    try {
      if (attachments.some(a => !!a.uploadPromise)) {
        setWaitingForAttachmentUpload(true);
        await Promise.all(attachments.map(a => a.uploadPromise));
        setWaitingForAttachmentUpload(false);
      }
      const emailAttachments = (
        await Promise.all(
          attachments
            .filter(a => !a.deleted && !a.uploadError)
            .map(async a => ({ id: a.id || (await a.uploadPromise) })),
        )
      ).filter(a => !!a.id);
      const payload = {
        subject: subjectValue,
        previewText: previewTextValue,
        body: html,
        emailAttachments,
        cc: ccEmails.join(','),
        bcc: bccEmails.join(','),
      };

      await commonWidgetHandlers.update({
        ...widget,
        config: {
          ...widget.config,
          ...payload,
        },
      });

      successToast('Your changes have been saved.', 'top-left');

      setAttachments(attachments =>
        attachments
          .filter(a => !a.deleted && !a.uploadError)
          .map(a => ({ ...a, added: undefined })),
      );
      cache.current = payload;
      editor.setDoc(editor.state.doc);
    } catch (ee) {
      errorToast('Something went wrong', 'top-left');
    } finally {
      setBusy(false);
    }
  };

  const hasSubjectPreviewChanges = useMemo(() => {
    return (
      cache.current.subject !== subjectValue ||
      cache.current.previewText !== previewTextValue
    );
  }, [
    subjectValue,
    previewTextValue,
    cache.current?.subject,
    cache.current?.previewText,
  ]);

  const hasBodyChanges = useMemo(() => {
    return compareNodes(editor.doc, editor.state.doc);
  }, [editor.state, editor.doc]);

  const [attachments, setAttachments] = useState<Attachment[]>(
    ((widget.config as any as EmailConfig).emailAttachments || []).map(a => ({
      uniqId: getUniqId('email-attachment'),
      id: a.id,
      name: a.originalFileName,
    })),
  );
  const activeAttachments = attachments.filter(a => !a.deleted);
  const hasMaxAttachments = activeAttachments.length >= MAX_ATTACHMENTS;
  const hasAttachmentChanges = attachments.some(a => a.deleted || a.added);

  const hasCChanges = ccEmails.join(',') !== (cc || '');
  const hasBCChanges = bccEmails.join(',') !== (bcc || '');

  const hasNoChanges = [
    hasSubjectPreviewChanges,
    hasBodyChanges,
    hasAttachmentChanges,
    hasBCChanges,
    hasCChanges,
  ].every(v => !v);

  const isSaveDisabled = busy;

  const onBeforeClose = () => {
    if (hasNoChanges) {
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      modalActions.openNewConfirmModal({
        title: 'You have unsaved changes',
        subtitle: 'Are you sure you want to leave without saving?',
        onReject: async () => {
          closeSlideout();
          reject();
        },
        onResolve: async close => {
          await onSave();
          close();
          resolve(true);
        },
        rejectButton: { children: 'Leave without saving' },
        resolveButton: {
          children: 'Save changes',
        },
      });
    });
  };

  const close = async () => {
    try {
      await onBeforeClose();
      closeSlideout();
    } catch (err) {}
  };

  const { setRef, close: closeSlideout } = useSlideoutOnClose(onBeforeClose);

  const onRemoveAttachment = (attachmentUniqId: string) => {
    setAttachments(as =>
      as.map(a =>
        a.uniqId === attachmentUniqId ? { ...a, deleted: true } : a,
      ),
    );
  };

  const {
    getRootProps,
    getInputProps,
    open: openAttachmentPrompt,
  } = useDropzone({
    maxSize: MAX_FILE_SIZE,
    accept: ACCEPTED_FILE_TYPES,
    onDrop: (files: File[], rejectedFiles: File[]) => {
      if (files.length) {
        if (activeAttachments.length + files.length > MAX_ATTACHMENTS) {
          modalActions.openGeneralTextModal({
            header: 'Cannot add attachments',
            message: `Maximum of ${MAX_ATTACHMENTS} attachments per email message`,
          });
          return;
        }
        for (const file of files) {
          const uniqId = getUniqId('email-attachment');
          const attachment = {
            uniqId,
            name: file.name,
            added: true,
            uploadPromise: uploadActions.uploadEmailAttachment(
              {
                content: file,
                extension: getFormatFromType(file.type),
              },
              true,
            ),
          };
          setAttachments(as => [...as, attachment]);
          attachment.uploadPromise
            .then(id => {
              setAttachments(as =>
                as.map(a =>
                  a.uniqId === uniqId
                    ? { ...a, id, uploadPromise: undefined }
                    : a,
                ),
              );
              return id;
            })
            .catch(err => {
              setAttachments(as =>
                as.map(a =>
                  a.uniqId === uniqId
                    ? {
                        ...a,
                        uploadPromise: undefined,
                        uploadError: err.toString(),
                      }
                    : a,
                ),
              );
            });
        }
      } else if (rejectedFiles.length) {
        if (rejectedFiles[0].size > MAX_FILE_SIZE) {
          modalActions.openGeneralTextModal({
            header: 'Cannot add attachment',
            message: `Attachment size is too big - maximum file size is ${MAX_FILE_SIZE_IN_MB}MB`,
          });
        } else {
          modalActions.openGeneralTextModal({
            header: 'Cannot add attachment',
            message: 'Unsupported file type',
          });
        }
      }
    },
  });

  const imageUploadSelectorRef = useRef<HTMLInputElement>(null);

  const viewActions = useViewActions();

  const handleGeneratedEmailPaste = (text: string) => {
    editor.insertText(text);
  };

  const onGenerateButtonClick = () =>
    viewActions.showSlideoutMenu('automationEmailGenerate', {
      onSubmit: handleGeneratedEmailPaste,
      getCurrentEmailString: () => {
        return editor.getHTMLStringFromState(editor.state);
      },
    });

  const {
    handleDrop,
    handlePaste,
    onImageSelected,
    imagesScaledDown,
    maxImageWidth,
    maxImageHeight,
  } = useEditorImageHandlers(editorRef.current?.view);

  const renderTitle = title || <Heading size={4}>Edit an Email</Heading>;

  const [userWritesManually, setUserWritesManually] = useState<null | boolean>(
    null,
  );

  const showGeneratePrompt =
    hasAccessToCompletions &&
    !cache?.current?.body &&
    userWritesManually === null;

  return (
    <Menu className={classNames(className, css.container)} setRef={setRef}>
      <div className={css.header}>
        {renderTitle}

        <div className={css.headerButtons}>
          <Button
            color="secondary"
            css={{ container: css.send }}
            onClick={onSendTestEmailClick}
          >
            Send me test email
          </Button>
          {navigateWidget && (
            <Button
              color="inverted-red"
              leftIcon={<DeleteOutlined />}
              css={{ container: css.delete }}
              onClick={onDeleteClick}
            />
          )}
          <Button
            css={{ container: css.delete }}
            onClick={onSave}
            disabled={isSaveDisabled}
          >
            Save
          </Button>
          <span className={css.close} onClick={close}>
            <CloseOutlined />
          </span>
        </div>
      </div>
      {showSmartSendingInfo && <SmartSendingInfo />}

      <Menu.Content className={css.content}>
        <div className={css.field}>
          <span className={css.fieldName}>Email subject</span>
          <AttributeContainingTextInput
            className={css.input}
            value={subjectValue}
            onChange={onChangeSubject}
            attributes={attributes}
            inputRef={setEmailSubjectRef}
          >
            <div className={css.inputActions}>
              <EmojiPicker
                className={css.emoji}
                onEmojiPicked={updateInsertEmojiSubject}
              >
                <SmileOutlined />
              </EmojiPicker>

              <AttributePicker
                className={css.picker}
                onAttributePicked={updateInsertAttributeSubject}
              >
                <BracketsOutlined className={css.code} />
              </AttributePicker>
            </div>
          </AttributeContainingTextInput>
        </div>
        <div className={css.field}>
          <span className={css.fieldName}>
            Email preview
            <Tooltip anchorElement={<QuestionCircleOutlined />}>
              The lighter text that is shown after the subject in your
              recipients' inboxes
            </Tooltip>
          </span>
          <AttributeContainingTextInput
            className={css.input}
            value={previewTextValue}
            onChange={onChangePreviewText}
            attributes={attributes}
            inputRef={setEmailPreviewTextRef}
          >
            <div className={css.inputActions}>
              <EmojiPicker
                className={css.emoji}
                onEmojiPicked={updateInsertEmojiPreviewText}
              >
                <SmileOutlined />
              </EmojiPicker>

              <AttributePicker
                className={css.picker}
                onAttributePicked={updateInsertAttributePreviewText}
              >
                <BracketsOutlined className={css.code} />
              </AttributePicker>
            </div>
          </AttributeContainingTextInput>
        </div>

        <div className={css.field}>
          <span className={css.fieldName}>Email body</span>

          <div className={css.toolbar}>
            <BoldOutlined
              className={classNames(css.editorButton, {
                [css.active]: editor.isBold(editor.state),
              })}
              onClick={() =>
                editor.toggleBold(editor.state, tr =>
                  editor.setState(editor.state.apply(tr)),
                )
              }
            />

            <ItalicOutlined
              className={classNames(css.editorButton, {
                [css.active]: editor.isItalic(editor.state),
              })}
              onClick={() =>
                editor.toggleItalic(editor.state, tr =>
                  editor.setState(editor.state.apply(tr)),
                )
              }
            />

            <UnderlineOutlined
              className={classNames(css.editorButton, {
                [css.active]: editor.isUnderline(editor.state),
              })}
              onClick={() =>
                editor.toggleUnderline(editor.state, tr =>
                  editor.setState(editor.state.apply(tr)),
                )
              }
            />

            <StrikethroughOutlined
              className={classNames(css.editorButton, {
                [css.active]: editor.isStrikethrough(editor.state),
              })}
              onClick={() =>
                editor.toggleStrikethrough(editor.state, tr =>
                  editor.setState(editor.state.apply(tr)),
                )
              }
            />

            <span className={css.separator} />

            <PaperClipOutlined
              className={classNames(css.editorButton, {
                [css.editorButtonDisabled]: hasMaxAttachments,
              })}
              title={
                hasMaxAttachments
                  ? `Maximum of ${MAX_ATTACHMENTS} attachments per email message`
                  : ''
              }
              onClick={() => {
                if (!hasMaxAttachments) {
                  openAttachmentPrompt();
                }
              }}
            />
            <EmojiPicker
              className={classNames(css.emoji, css.editorButton)}
              onEmojiPicked={emoji => {
                editor.insertText(emoji);
                editorRef.current?.view?.focus?.();
              }}
            >
              <SmileOutlined />
            </EmojiPicker>

            <LinkOutlined
              className={classNames(css.editorButton, {
                [css.active]: editor.isLink(editor.state),
              })}
              onClick={() =>
                editor.toggleLink(editor.state, tr =>
                  editor.setState(editor.state.apply(tr)),
                )
              }
            />

            <UnorderedListOutlined
              className={classNames(css.editorButton, {
                [css.active]: editor.isUnorderedList(editor.state),
              })}
              onClick={() =>
                editor.toggleUnorderedList(editor.state, tr =>
                  editor.setState(editor.state.apply(tr)),
                )
              }
            />

            <OrderedListOutlined
              className={classNames(css.editorButton, {
                [css.active]: editor.isOrderedList(editor.state),
              })}
              onClick={() =>
                editor.toggleOrderedList(editor.state, tr =>
                  editor.setState(editor.state.apply(tr)),
                )
              }
            />

            <FileImageOutlined
              className={classNames(css.editorButton, {
                [css.editorButtonDisabled]: !editor.canInsertImageAtCursor(
                  editor.state,
                ),
              })}
              onClick={() => {
                if (editor.canInsertImageAtCursor(editor.state)) {
                  imageUploadSelectorRef.current?.click();
                }
              }}
            />
          </div>

          <div className={css.attributes}>
            <span className={css.attributesTitle}>Insert attributes:</span>
            <div className={css.attributesList}>
              {attributes.slice(0, 5).map(attr => (
                <Chip
                  key={attr.id}
                  text={attr.name}
                  type="info"
                  onClick={() => {
                    const name = nameById(attr.id, attributes, '', true);
                    insertBodyAttribute(name);
                    editorRef.current?.view?.focus?.();
                  }}
                  className={css.attribute}
                />
              ))}
              <AttributePicker
                className={css.picker}
                onAttributePicked={insertBodyAttribute}
              >
                <Chip type="info" text="Other" className={css.attribute}>
                  <CaretDownFilled />
                </Chip>
              </AttributePicker>
            </div>
          </div>

          <div className={css.dropzone} {...getRootProps()}>
            <input {...getInputProps()} />
          </div>
          <div className={css.imageUpload}>
            <input
              type="file"
              ref={imageUploadSelectorRef}
              onChange={onImageSelected}
              accept="image/*"
              multiple
            />
          </div>

          <div className={css.editorContainer}>
            {showGeneratePrompt && (
              <div className={css.generatePrompt}>
                <Button
                  css={{ container: css.generateButton }}
                  rightIcon={<MagicPencilIcon />}
                  onClick={() => {
                    setUserWritesManually(false);
                    onGenerateButtonClick();
                  }}
                >
                  Write an email for me
                </Button>
                <Button
                  color="transparent-no-shadow"
                  onClick={() => {
                    setUserWritesManually(true);
                    editorRef.current?.view?.focus?.();
                  }}
                >
                  I want to write this email on my own
                </Button>
              </div>
            )}

            <ProseMirror
              ref={editorRef}
              className={css.editor}
              state={editor.state}
              onChange={editor.setState}
              handleDrop={handleDrop}
              handlePaste={handlePaste}
            />
            {!!activeAttachments.length && (
              <div className={css.attachments}>
                {activeAttachments.map((attachment, i) => (
                  <div key={attachment.uniqId} className={css.attachment}>
                    <div>{attachment.name}</div>
                    <div className={css.attachmentStatus}>
                      {!!attachment.uploadPromise && (
                        <Spinner standalone size={8} />
                      )}
                      {!!attachment.uploadError && (
                        <ErrorIcon color={colors.red} />
                      )}
                      <CloseOutlined
                        onClick={() => onRemoveAttachment(attachment.uniqId)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {editor.link && (
              <LinkPrompt
                link={editor.link}
                onSubmit={link => {
                  editor.updateLink(
                    editor.state,
                    tr => editor.setState(editor.state.apply(tr)),
                    link,
                  );
                }}
                onCancel={editor.closeLink}
              />
            )}

            {hasAccessToCompletions && (
              <Button
                css={{ container: css.generateButton }}
                rightIcon={<MagicPencilIcon />}
                onClick={onGenerateButtonClick}
              >
                Generate Email
              </Button>
            )}
          </div>

          {showSentFromMMWarning && (
            <div className={css.freeTrialFooter}>
              Emails sent from Free Trial accounts have
              <br />
              "Sent from my MobileMonkey" branding.{' '}
              <Button color="link" onClick={onUpgradeClick}>
                Upgrade now to remove
              </Button>
            </div>
          )}
          {imagesScaledDown && (
            <Alert
              type="warning"
              showCloseIcon={false}
              className={css.bottomAlert}
            >
              Large images are automatically scaled down to a maximum width of{' '}
              {maxImageWidth}px and a maximum height of {maxImageHeight}px.
            </Alert>
          )}
        </div>

        <div className={css.field}>
          <span className={css.fieldName}>Optional email(s) to CC:</span>

          <Input
            value={ccDisabled ? ccMaxReachText : ccInputValue}
            placeholder={emailInputPlaceholder}
            disabled={ccDisabled}
            css={{ container: classNames(css.input, css.ccInput) }}
            onChange={e => setCcInputValue(e.target.value || '')}
            onSearch={handleCCSubmit}
          />

          {!!ccEmails.length && (
            <div className={css.emailList}>
              {ccEmails.map(email => (
                <Chip type="info" text={email} key={email}>
                  <CloseOutlined
                    className={css.emailDeleteIcon}
                    onClick={() => handleCCDelete(email)}
                  />
                </Chip>
              ))}
            </div>
          )}

          <span className={css.fieldHelper}>
            Up to {CC_CAP} emails separated with a comma
          </span>
        </div>

        <div className={css.field}>
          <span className={css.fieldName}>Optional email(s) to BCC:</span>

          <Input
            value={bccDisabled ? bccMaxReachText : bccInputValue}
            placeholder={emailInputPlaceholder}
            disabled={bccDisabled}
            css={{ container: classNames(css.input, css.ccInput) }}
            onChange={e => setBccInputValue(e.target.value || '')}
            onSearch={handleBCCSubmit}
          />

          {!!bccEmails.length && (
            <div className={css.emailList}>
              {bccEmails.map(email => (
                <Chip type="info" text={email} key={email}>
                  <CloseOutlined
                    className={css.emailDeleteIcon}
                    onClick={() => handleBCCDelete(email)}
                  />
                </Chip>
              ))}
            </div>
          )}

          <span className={css.fieldHelper}>
            Up to {CC_CAP} emails separated with a comma
          </span>
        </div>
      </Menu.Content>
      {waitingForAttachmentUpload && (
        <div className={css.waitingForAttachmentUploadWrapper}>
          <div className={css.waitingForAttachmentUpload}>
            <Spinner standalone size={16} />
            <div>Waiting for attachments to finish uploading...</div>
          </div>
        </div>
      )}
    </Menu>
  );
};

export const SlideoutEmail: React.FC<ISlideoutEmail> = props => {
  const { dialogueId, navigateWidget } = props;

  const { setRef, close } = useSlideoutOnClose();

  const widgets = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(dialogueId, state.widget.entities),
  );

  const widget = widgets.find(w => w.type === WidgetTypes.EMAIL);

  if (!widget) {
    return (
      <Menu setRef={setRef}>
        <Menu.Header className="" closeIcon="close">
          Ooops..
        </Menu.Header>
        <Menu.Content>Something went wrong</Menu.Content>
      </Menu>
    );
  }

  const config = widget.config as unknown as EmailConfig;

  // temp disabled per request
  // if (
  //   [config.subject, config.body, config.previewText].every(
  //     attr => attr === null,
  //   )
  // ) {
  //   return <SlideoutEmailTemplateList widget={widget} {...props} />;
  // }

  return (
    <SlideoutEmailEditor
      navigateWidget={navigateWidget}
      widget={widget}
      {...props}
    />
  );
};
